import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2, Flag, ChevronDown, Building2, Contact as ContactIcon, TrendingUp, Bell, User, Target, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRIORITY_COLORS = {
  high: "text-brand-red bg-red-500/10 ring-red-500/30",
  medium: "text-brand-orange bg-orange-500/10 ring-orange-500/30",
  low: "text-muted-foreground bg-muted ring-border",
};

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function WorkEngine() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("today");
  const [scope, setScope] = useState("my"); // my | all
  const [form, setForm] = useState({ title: "", priority: "medium", start_at: "", reminder_at: "", due_at: "" });
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, ct, d, m, p, g] = await Promise.all([
        api.get("/tasks"),
        api.get("/companies"),
        api.get("/contacts"),
        api.get("/deals"),
        api.get("/organizations/me/members").catch(() => ({ data: { members: [] } })),
        api.get("/projects").catch(() => ({ data: { projects: [] } })),
        api.get("/goals").catch(() => ({ data: { goals: [] } })),
      ]);
      setTasks(t.data.tasks || []);
      setCompanies(c.data.companies || []);
      setContacts(ct.data.contacts || []);
      setDeals(d.data.deals || []);
      setMembers(m.data.members || []);
      setProjects(p.data.projects || []);
      setGoals(g.data.goals || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load tasks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, changes) => {
    try {
      const { data } = await api.patch(`/tasks/${id}`, changes);
      setTasks((all) => all.map((x) => (x.id === id ? data.task : x)));
      return data.task;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update task.");
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      const payload = { title: form.title.trim(), priority: form.priority };
      // Send naive workspace-local datetime strings; backend converts to UTC using workspace tz.
      if (form.start_at) payload.start_at = form.start_at;
      if (form.reminder_at) payload.reminder_at = form.reminder_at;
      if (form.due_at) payload.due_at = form.due_at;
      const { data } = await api.post("/tasks", payload);
      setTasks((t) => [data.task, ...t]);
      setForm({ title: "", priority: "medium", start_at: "", reminder_at: "", due_at: "" });
      toast.success("Task added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create task.");
    }
  };

  const toggleDone = (task) => patch(task.id, { status: task.status === "done" ? "todo" : "done" });

  const removeTask = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks((t) => t.filter((x) => x.id !== id));
    } catch { toast.error("Could not delete task."); }
  };

  const filtered = useMemo(() => {
    const t = todayISO();
    let base = tasks;
    if (scope === "my") base = base.filter((x) => (x.assignee_id || x.user_id) === user?.user_id);
    if (filter === "today") return base.filter((x) => (x.due_at || "").slice(0, 10) === t && x.status !== "done");
    if (filter === "overdue") return base.filter((x) => x.due_at && x.due_at.slice(0, 10) < t && x.status !== "done");
    if (filter === "upcoming") return base.filter((x) => x.due_at && x.due_at.slice(0, 10) > t && x.status !== "done");
    if (filter === "done") return base.filter((x) => x.status === "done");
    return base.filter((x) => x.status !== "done");
  }, [tasks, filter, scope, user?.user_id]);

  const counts = useMemo(() => {
    const t = todayISO();
    const base = scope === "my" ? tasks.filter((x) => (x.assignee_id || x.user_id) === user?.user_id) : tasks;
    return {
      today: base.filter((x) => (x.due_at || "").slice(0, 10) === t && x.status !== "done").length,
      overdue: base.filter((x) => x.due_at && x.due_at.slice(0, 10) < t && x.status !== "done").length,
      upcoming: base.filter((x) => x.due_at && x.due_at.slice(0, 10) > t && x.status !== "done").length,
      done: base.filter((x) => x.status === "done").length,
      all: base.filter((x) => x.status !== "done").length,
    };
  }, [tasks, scope, user?.user_id]);

  return (
    <div data-testid="work-engine">
      <Seo title="Work" description="Tasks, priorities and follow-ups." path="/app/work" />
      <header className="mb-6">
        <p className="eyebrow text-brand-teal">Work Engine</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Everything on your plate.</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {counts.all} open · {counts.overdue} overdue · {counts.done} completed
        </p>
        {members.length > 1 && (
          <div className="mt-4 inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" data-testid="work-scope-toggle">
            <button
              onClick={() => setScope("my")}
              data-testid="work-scope-my"
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${scope === "my" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
            >
              My Work
            </button>
            <button
              onClick={() => setScope("all")}
              data-testid="work-scope-all"
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${scope === "all" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
            >
              All Work
            </button>
          </div>
        )}
      </header>

      <form onSubmit={createTask} className="mb-6 rounded-2xl border border-border bg-card p-3" data-testid="task-form">
        <div className="flex flex-wrap items-center gap-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Add a task…" className={`${inputCls} flex-1 min-w-[220px]`} data-testid="task-title-input" />
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={`${inputCls} sm:w-36`} data-testid="task-priority-select">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <button type="submit" data-testid="task-add-btn" className="inline-flex items-center gap-1.5 rounded-xl brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          <label className="block text-[11px] font-medium text-muted-foreground">
            Start date &amp; time
            <input
              type="datetime-local"
              value={form.start_at}
              onChange={(e) => setForm({ ...form, start_at: e.target.value })}
              className={`${inputCls} mt-0.5`}
              data-testid="task-start-input"
            />
          </label>
          <label className="block text-[11px] font-medium text-muted-foreground">
            Reminder date &amp; time
            <input
              type="datetime-local"
              value={form.reminder_at}
              onChange={(e) => setForm({ ...form, reminder_at: e.target.value })}
              className={`${inputCls} mt-0.5`}
              data-testid="task-reminder-input"
            />
          </label>
          <label className="block text-[11px] font-medium text-muted-foreground">
            Due date &amp; time
            <input
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => setForm({ ...form, due_at: e.target.value })}
              className={`${inputCls} mt-0.5`}
              data-testid="task-due-input"
            />
          </label>
        </div>
      </form>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="task-filters">
        {[
          ["today", "Today", counts.today],
          ["upcoming", "Upcoming", counts.upcoming],
          ["overdue", "Overdue", counts.overdue],
          ["all", "All open", counts.all],
          ["done", "Completed", counts.done],
        ].map(([k, label, n]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`task-filter-${k}`}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
            <span className={`rounded-full px-1.5 text-[10px] ${filter === k ? "bg-white/20" : "bg-muted"}`}>{n}</span>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="task-list">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No tasks in this view.</p>
        ) : (
          filtered.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              companies={companies}
              contacts={contacts}
              deals={deals}
              members={members}
              projects={projects}
              goals={goals}
              expanded={expandedId === t.id}
              onExpand={() => setExpandedId(expandedId === t.id ? null : t.id)}
              onToggle={() => toggleDone(t)}
              onDelete={() => removeTask(t.id)}
              onPatch={(chg) => patch(t.id, chg)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, companies, contacts, deals, members, projects, goals, expanded, onExpand, onToggle, onDelete, onPatch }) {
  const co = companies.find((c) => c.id === task.company_id);
  const ct = contacts.find((c) => c.id === task.contact_id);
  const dl = deals.find((d) => d.id === task.deal_id);
  const assignee = (members || []).find((m) => m.user_id === (task.assignee_id || task.user_id));
  const relatedGoal = (goals || []).find((g) => g.id === task.related_goal_id);
  return (
    <div className="px-4 py-3" data-testid={`task-row-${task.id}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={onToggle}
          data-testid={`task-toggle-${task.id}`}
          className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border ${task.status === "done" ? "border-brand-teal bg-brand-teal text-white" : "border-border"}`}
        >
          {task.status === "done" && <Check className="h-3.5 w-3.5" />}
        </button>
        <button onClick={onExpand} data-testid={`task-expand-${task.id}`} className="min-w-0 flex-1 text-left">
          <p className={`truncate text-sm ${task.status === "done" ? "text-muted-foreground line-through" : "text-foreground"}`}>{task.title}</p>
          <div className="mt-0.5 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
            {task.source === "board" && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary" data-testid={`from-board-badge-task-${task.id}`}>
                <Sparkles className="h-2.5 w-2.5" /> From Board
              </span>
            )}
            {task.start_at && <span className="inline-flex items-center gap-1"><Flag className="h-3 w-3" /> Start {task.start_at.slice(0, 16).replace("T", " ")}</span>}
            {task.reminder_at && <span className="inline-flex items-center gap-1"><Bell className="h-3 w-3" /> {task.reminder_at.slice(0, 16).replace("T", " ")}</span>}
            {task.due_at && <span>Due {task.due_at.length >= 16 ? task.due_at.slice(0, 16).replace("T", " ") : task.due_at.slice(0, 10)}</span>}
            {assignee && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {assignee.name || assignee.email}</span>}
            {relatedGoal && <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {relatedGoal.name}</span>}
            {co && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {co.name}</span>}
            {ct && <span className="inline-flex items-center gap-1"><ContactIcon className="h-3 w-3" /> {ct.name}</span>}
            {dl && <span className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {dl.title}</span>}
          </div>
        </button>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>
          <Flag className="h-3 w-3" /> {task.priority}
        </span>
        <button onClick={onExpand} className="text-muted-foreground hover:text-foreground" aria-label="Details">
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
        <button onClick={onDelete} data-testid={`task-delete-${task.id}`} className="text-muted-foreground hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 grid gap-3 rounded-xl bg-background/60 p-3 md:grid-cols-2" data-testid={`task-detail-${task.id}`}>
          <label className="col-span-full block text-xs font-medium text-muted-foreground">
            Description
            <textarea
              rows={2}
              defaultValue={task.description || ""}
              onBlur={(e) => e.target.value !== (task.description || "") && onPatch({ description: e.target.value })}
              className={`${inputCls} mt-1 resize-none`}
              data-testid={`task-description-${task.id}`}
              placeholder="Details, context, links…"
            />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Start date & time
            <input
              type="datetime-local"
              defaultValue={task.start_at ? task.start_at.slice(0, 16) : ""}
              onBlur={(e) => {
                const v = e.target.value || null;
                if (v !== (task.start_at || null)) onPatch({ start_at: v });
              }}
              className={`${inputCls} mt-1`}
              data-testid={`task-start-${task.id}`}
            />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Reminder date & time
            <input
              type="datetime-local"
              defaultValue={task.reminder_at ? task.reminder_at.slice(0, 16) : ""}
              onBlur={(e) => {
                const v = e.target.value || null;
                if (v !== (task.reminder_at || null)) onPatch({ reminder_at: v });
              }}
              className={`${inputCls} mt-1`}
              data-testid={`task-reminder-${task.id}`}
            />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Due date &amp; time
            <input
              type="datetime-local"
              defaultValue={task.due_at ? (task.due_at.length >= 16 ? task.due_at.slice(0, 16) : `${task.due_at.slice(0, 10)}T00:00`) : ""}
              onBlur={(e) => {
                const v = e.target.value || null;
                if (v !== (task.due_at || null)) onPatch({ due_at: v });
              }}
              className={`${inputCls} mt-1`}
              data-testid={`task-duedate-${task.id}`}
            />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Related company
            <select
              defaultValue={task.company_id || ""}
              onChange={(e) => onPatch({ company_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`task-company-${task.id}`}
            >
              <option value="">—</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Related contact
            <select
              defaultValue={task.contact_id || ""}
              onChange={(e) => onPatch({ contact_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`task-contact-${task.id}`}
            >
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Related deal
            <select
              defaultValue={task.deal_id || ""}
              onChange={(e) => onPatch({ deal_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`task-deal-${task.id}`}
            >
              <option value="">—</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Assignee
            <select
              value={task.assignee_id || ""}
              onChange={(e) => onPatch({ assignee_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`task-assignee-${task.id}`}
            >
              <option value="">Unassigned</option>
              {(members || []).map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Project
            <select
              value={task.project_id || ""}
              onChange={(e) => onPatch({ project_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`task-project-${task.id}`}
            >
              <option value="">—</option>
              {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Related goal
            <select
              value={task.related_goal_id || ""}
              onChange={(e) => onPatch({ related_goal_id: e.target.value || null })}
              className={`${inputCls} mt-1`}
              data-testid={`task-goal-${task.id}`}
            >
              <option value="">—</option>
              {(goals || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
